const React = window.React || null;
const { useState, useEffect, useRef } = React || null;


interface Alert {
    // Define the structure of your alert object here
    alertUrl: string;
    alertRichText: string;
    alertRichTextMobile: string;
    renderInNav: boolean;
    openInNewTab: boolean;
}

const delay = 8000;
const duration = 400;
const transitionDuration = 300;
const mobileBreakpoint = 991;

export default function NavAlertsApp({ containerType = 'desktop' }: { containerType?: 'desktop' | 'mobile' } = {}){

    const [alerts, setAlerts] = useState<Alert[]>([]);
    const [isMobile, setIsMobile] = useState(false);

    const fetchAlerts = ( { mobileOnly = false } : { mobileOnly?: Boolean } = {} ) => {
        const elAlertBlocks = document.querySelectorAll('.adw-alert');

        if( elAlertBlocks.length > 0 ) {
            const alertsFromBlocks : any = [];
            elAlertBlocks.forEach((elBlock) => {
                
                if( (elBlock as HTMLElement)?.dataset?.attributes ) {
                    const attributesAsStr = elBlock?.getAttribute?.('data-attributes') || '';
                    const attributes = JSON.parse( attributesAsStr );

                    // Skip if the block is not set to render in the nav
                    if( !attributes.renderInNav ) {
                        return;
                    }

                    if( mobileOnly && !attributes.alertRichTextMobile ) {
                        return;
                    }

                    // Skip if the block is already in the alerts array
                    if( alertsFromBlocks?.length && alertsFromBlocks.includes(attributes) ) {
                        return;
                    }
                    alertsFromBlocks.push(attributes);
                }
            });

            return alertsFromBlocks;
        }
        return [];
    }
    
    useEffect(() => {
        // Check if we're on mobile
        const checkMobile = () => {
            setIsMobile(window.innerWidth < mobileBreakpoint);
        };
        
        checkMobile();
        window.addEventListener('resize', checkMobile);
        
        return () => window.removeEventListener('resize', checkMobile);
    }, []);

    useEffect(() => {
        // Fetch alerts from the DOM
        const alerts = isMobile ? fetchAlerts( { mobileOnly: true } ) : fetchAlerts();
        setAlerts(alerts);
        
    }, [isMobile])

    // Re-fetch alerts on window resize,
    // and if mobile, exclude alerts that do not have the mobile text (alert.alertRichTextMobile)

    useEffect(() => {
        const handleResize = ( event ) => {
            
            let alerts = [];
            // If the screen width is less than mobileBreakpoint px, filter out alerts that do not have mobile text
            if( isMobile ) {
                alerts = fetchAlerts( { mobileOnly: true } );
            } else {
                // If the screen width is greater than mobileBreakpoint px, fetch all alerts
                alerts = fetchAlerts();
            }
            setAlerts(alerts);

        }

        window.addEventListener('resize', handleResize, { passive: true });

        return () => {
            window.removeEventListener('resize', handleResize);
        }
    }, [isMobile])

    // Only render if the container type matches the current viewport
    // Mobile container should only render on mobile
    // Desktop container should only render on desktop
    if ((containerType === 'mobile' && !isMobile) || (containerType === 'desktop' && isMobile)) {
        return null;
    }

    return (
        <div className="adw-nav-alerts-app d-flex align-items-center justify-content-center justify-content-lg-start ">

            {/* Alerts Component */}
            <Alerts alerts={alerts} />

        </div>
    )

}

function Alerts({ alerts }: { alerts: Alert[] }) {
    
    const [activeIndex, setActiveIndex] = useState(0);

    
    // Cycle active index through alerts
    useEffect(() => {
        if( alerts.length <= 1 ) return;
        const interval = setInterval(() => {
            setActiveIndex((prevIndex) => (prevIndex + 1) % alerts.length);
        }, delay);
        return () => clearInterval(interval);
    }, [alerts.length]);

    return (
        <div className="adw-alerts d-flex flex-column gap-3 overflow-hidden">
            {alerts.map((alert, index) => (
                <AlertItem key={index} index={index} alert={alert} activeIndex={activeIndex} hasMultipleAlerts={alerts.length > 1} />
            ))}
        </div>
    );
}

function AlertItem({ alert, activeIndex, index, hasMultipleAlerts }: { alert: Alert, activeIndex: number, index: number, hasMultipleAlerts: boolean }) {
    
    const ref = useRef<HTMLDivElement>(null);

    // Apply animation if there are multiple alerts
    useEffect(() => {

        if( alert && activeIndex === index && hasMultipleAlerts ){
            if( 'function' === typeof anime ){
                const elAnchor = ref?.current?.querySelector( 'a' );

                // Fade in the alert
                anime({
                    targets: elAnchor,
                    opacity: [0, 1],
                    duration: duration,
                    easing: 'easeOutQuad',
                });

                const elProgress = ref?.current?.querySelector( '.progress-nav-alert' );
                anime?.set(elProgress, {
                    transformOrigin: '0 0',
                });
                anime({
                    targets: elProgress,
                    scaleX: ['0%', '100%'],
                    duration: delay - transitionDuration - 50,
                    delay: 50,
                    easing: 'linear',
                })

                anime({
                    targets: ref?.current,
                    opacity: [1, 0],
                    duration: transitionDuration,
                    delay: delay - 300,
                    easing: 'easeOutQuad',
                })
            }

        }

    }, [alert, activeIndex]);

    const AlertIcon = () => (
        <svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M15.9994 29C23.1745 29 29 23.1745 29 15.9993C29 8.82416 23.1745 3 15.9994 3C8.82546 3 3 8.82416 3 15.9993C3 23.1745 8.82546 29 15.9994 29ZM15.9994 27.0495C9.90083 27.0495 4.95049 22.0979 4.95049 15.9993C4.95049 9.90082 9.90083 4.95049 15.9994 4.95049C22.0979 4.95049 27.0495 9.90082 27.0495 15.9993C27.0495 22.0979 22.0979 27.0495 15.9994 27.0495ZM15.9994 18.5974C15.461 18.5974 15.0241 18.1605 15.0241 17.6222V10.4704C15.0241 9.93203 15.461 9.49512 15.9994 9.49512C16.5377 9.49512 16.9746 9.93203 16.9746 10.4704V17.6222C16.9746 18.1605 16.5377 18.5974 15.9994 18.5974ZM15.9967 22.4984C16.7145 22.4984 17.2971 21.9158 17.2971 21.198C17.2971 20.4803 16.7145 19.8977 15.9967 19.8977C15.279 19.8977 14.6964 20.4803 14.6964 21.198C14.6964 21.9158 15.279 22.4984 15.9967 22.4984Z" fill="#F53C60"/>
        </svg>
    );

    return (
        <>
            <div ref={ref} data-is-active={activeIndex === index}
                className={`adw-nav-alert ${ activeIndex === index ? 'd-flex' : 'd-none' } flex-column border border-primary rounded-end-top-3 `}
            >
                <div className="py-2 py-lg-3 px-3 d-flex align-items-center">
                    <a href={alert.alertUrl || '#'} target={alert?.openInNewTab ? '_blank' : '_self'} className="text-decoration-none d-block d-md-none overflow-hidden fs-0 mobile-nav-alert">
                        <div className="mobile-alert-container d-flex align-items-center">
                            <div className="alert-icon me-3">
                                <AlertIcon />
                            </div>
                            <div className="alert-content">
                                <div className="alert-label">
                                    <span className="alert-brand">ADWEEK</span>
                                    <span className="alert-type">ALERT:</span>
                                </div>
                                <div 
                                    className="alert-message"
                                    dangerouslySetInnerHTML={{ __html: alert.alertRichTextMobile || alert.alertRichText }}
                                />
                            </div>
                        </div>
                    </a>
                    <a href={alert.alertUrl || '#'} target={alert?.openInNewTab ? '_blank' : '_self'} className="text-decoration-none d-none d-md-block overflow-hidden">
                        <div
                            style={{color: 'var(--bs-link-color)'}}
                            className="nav-alert-content lh-sm" dangerouslySetInnerHTML={{ __html: alert.alertRichText }}
                        />
                    </a>
                </div>

                { hasMultipleAlerts && (
                    <div className="progress-nav-alert border-bottom border-primary" style={{height: '1px'}}></div>
                )}
            </div>
        </>
    );
}